import {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {DateRange} from "react-day-picker";
import {
    Center,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    InputGroup,
    Input,
    InputRightElement,
    Switch,
    Text,
    Textarea,
    TextareaProps
} from "@chakra-ui/react";
import {DefaultProps, InputProps, InputStyle, Nullable, Option, SuggestionData, UserTagProps, TagUser} from "../../models";
import {CustomSelect} from "../UI";
import {CustomCloserButton} from "./CustomButton";
import {searchUsers} from "../../services/requests";
import {DateRangeModal} from "./CustomModal";
import {formatDate, notEmpty} from "../../helper/utils";
import {UserTagInput} from "./UserTagInput";
import {scrollbar} from "../styling";

interface Resetable {whenReset: () => void}
type DateRangeError = {start?: string, end?: string};
type WithChildren = {type: string, readonly?: boolean, children?: ReactNode };

const noSpace = (v: Nullable<string>) => v ? v.replaceAll(" ", "") : undefined;
const MIN_HEIGHT = 42;
const MAX_HEIGHT = 200;

function DefaultInput(p: InputProps<string> & WithChildren) {
    const htmlFor = p.id ?? noSpace(p.label);
    return <FormControl  isInvalid={notEmpty(p.error)}>
        <InputGroup flexDir="column" gap=".25rem">
            {p.label && <FormLabel mb="0" color="dark.500" htmlFor={htmlFor}>
                {p.label}
            </FormLabel>}
            {p.description && <Text
                fontSize="0.9em"
                color="gray.500"
                id={htmlFor + "_desc"}
            >{p.description}</Text>}
            <HStack position="relative" padding="2px">
                <Input
                    aria-describedby={p.description ? htmlFor + "_desc": undefined}
                    id={htmlFor}
                    px={p.px ?? "12px"}
                    py={p.py ?? "14px"}
                    borderRadius="5px"
                    backgroundColor="#F5F7F9"
                    type={p.type}
                    outline="none"
                    flex="2 1 auto"
                    value={p.currentValue ?? ""}
                    placeholder={p.placeholder}
                    onClick={p.onClick}
                    _hover={p._hover}
                    readOnly={p.readonly}
                    disabled={p.disabled}
                    onChange={function (e) {
                        const {value} = e.target;
                        if (p.onUpdate) {
                            p.onUpdate(value);
                        }
                    }}
                    height="58px"
                    focusBorderColor={p.error ? "error" : "primary.500"}
                    border={p.error ? "1px solid" : "none"}
                    borderColor={p.error ? "error" : "transparent"}
                />
                {p.children}
            </HStack>
            {p.error && <FormErrorMessage color="red" fontSize="14px" p={0} m={0} py="2px">
                {p.error}
            </FormErrorMessage>}
        </InputGroup>
    </FormControl>;
}

function SelectionInput(p: InputProps<string> & {options: Option[]}) {
    const id = p.id ?? noSpace(p.label);
    return <InputGroup flexDir="column" gap=".25rem" isolation="auto" >
        {p.label && <FormLabel color="dark.200" htmlFor={id}>{p.label}</FormLabel>}
        {p.description && <Text
            fontSize="0.9em"
            color="gray.500"
            id={id + "_desc"}
        >{p.description}</Text>}
        <CustomSelect
            id={id}
            defaultValue={p.defaultValue ?? p.placeholder}
            currentValue={p.currentValue ?? p.placeholder}
            textTransform="capitalize"
            header={p.title}
            p=".25rem .5rem"
            onSelect={function (id) {
                const c = p.options.filter((v) => v.id === id)[0];
                if (c && p.onUpdate) {
                    p.onUpdate(c.id);
                }
            }}
            options={p.options}
            name={p.name ?? "category"}
        />
    </InputGroup>
}

function CustomToggle(props: InputProps<boolean>) {
    const id = props.id ?? noSpace(props.label);
    const [checked, setChecked] = useState(props.currentValue ?? false);
    return <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={4}>
        <FormLabel htmlFor={id} flex="2 1 auto">{props.label}</FormLabel>
        <Switch id={id} size="md" colorScheme="primary" onChange={() => setChecked(!checked)} isChecked={checked}/>
    </FormControl>;
}

export function FormInput(props: InputStyle) {
    switch (props.type) {
        case "date_range":
            return <DateRangePicker {...props.data}/>;
        case "datetime": {
            return <DateTimePicker {...props.data}/>;
        }
        case "text":
        case "email": {
            return <DefaultInput type={props.type} {...props.data}/>
        }
        case "text+box":
            return <DefaultInput type="text" {...props.data}>
                <Center w="80px" h="40px" border="1px solid #E2E8F0" borderRadius="8px" px="8px" fontSize=".9em" >
                    {props.data.rightLabel}
                </Center>
            </DefaultInput>;
        case "select": {
            return <SelectionInput {...props.data}/>;
        }
        case "toggle": {
            return <CustomToggle {...props.data}/>;
        }
        case "users": {
            return <UserInput {...props.data}/>;
        }
        case "textarea": {
            return <CustomTextArea {...props.data}/>;
        }
        default: {
            return <Text>Type not supported</Text>
        }
    }
}

async function lookupUser(query: string): Promise<SuggestionData[]> {
    try {
        const data = await searchUsers(query);
        return data.map(function (user) {
            const {email: subtitle, name: title, profileImage: type} = user;
            return {subtitle, title, type, value: user};
        });
    } catch (error: any) {
        throw Error("Failed to fetch users, please try again later.");
    }
}

function UserInput(p: InputProps<TagUser[]> & UserTagProps) {
    const id = p.id ?? noSpace(p.label);
    return <InputGroup flexDir="column" gap=".25rem" isolation="auto">
        {p.label && <FormLabel mb="0" htmlFor={id} color="dark.200">{p.label}</FormLabel>}
        {p.description && <Text
            fontSize="0.9em"
            color="gray.500"
            id={id + "_desc"}
        >{p.description}</Text>}
        <UserTagInput
            {...p}
            id={id}
            fetchUsers={lookupUser}
            defineIcon={(d) => d.type}
        />
    </InputGroup>;
}


function DateTimePicker(props: InputProps<Date> & Resetable) {
    function format(date: Nullable<Date>): string {
        if (date) {
            return formatDate(date);
        }
        return "";
    }
    return <DefaultInput
        type="datetime"
        readonly={true}
        id={props.id}
        currentValue={format(props.currentValue)}
        label={props.label}
        placeholder={props.placeholder}
        onClick={props.onClick}
        error={props.error}
        _hover={{cursor: "pointer"}}
    >
        {props.currentValue && (
            <InputRightElement >
                <CustomCloserButton onClose={props.whenReset} size={16}/>
            </InputRightElement>
        )}
    </DefaultInput>;
}


function DateRangePicker(props: InputProps<DateRange> & {errors?: DateRangeError}) {
    const id = props.id ?? noSpace(props.label);
    const [opened, setOpened] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<DateRange|undefined>(
        props.currentValue
    );
    const  format = (d?: number | Date) => (
        d
            ? new Date(d).toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
                day: "2-digit"
            })
            : ""
    );
    const value = useMemo(function () {
        return {end: format(dateRange?.to), start: format(dateRange?.from)};
    }, [dateRange]);
    function handleChange(range: DateRange) {
        setDateRange(range);
        if (props.onUpdate) {
            props.onUpdate(range);
        }
    }
    return <>
        <DateRangeModal
            isOpen={opened}
            onClose={() => setOpened(false)}
            onSelectDateRange={handleChange}
            dateRange={dateRange}
            disabled={(d) => new Date().setHours(0,0,0,0) > d.setHours(0)}
        />
        <InputGroup flexDir="column" gap=".25rem" isolation="auto">
            {props.label && <FormLabel htmlFor={id} color="dark.200">{props.label}</FormLabel>}
            <HStack onClick={() => setOpened(true)} cursor="pointer">
                <DefaultInput
                    type="text"
                    id="start"
                    placeholder="Start Date"
                    readonly={true}
                    currentValue={value.start}
                    error={props.errors?.start}
                    pointerEvents="none"
                />
                <DefaultInput
                    type="text"
                    id="end"
                    placeholder="End Date"
                    readonly={true}
                    currentValue={value.end}
                    error={props.errors?.end}
                    pointerEvents="none"
                />
            </HStack>
        </InputGroup>
    </>
}

function CustomTextArea(p: DefaultProps<string> & TextareaProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const id = p.id ?? noSpace(p.label);
    const minH = p.mh ?? MIN_HEIGHT;
    function resizeTextarea() {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = `${minH}px`; // Reset to minimum
            const scrollHeight = textarea.scrollHeight;
            const clampedHeight = Math.min(
                Math.max(scrollHeight, minH),
                MAX_HEIGHT
            );
            textarea.style.height = `${clampedHeight}px`;
            textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
            p.onSizeExceed?.(clampedHeight > minH);
        }
    }
    useEffect(resizeTextarea, [p.currentValue]);
    return <FormControl isInvalid={notEmpty(p.error)}>
        <InputGroup flexDir="column" gap=".25rem">
            {p.label && <FormLabel mb="0" color="dark.500" htmlFor={id}>
                {p.label}
            </FormLabel>}
            {p.description && <Text
                fontSize="0.9em"
                color="gray.500"
                id={id + "_desc"}
            >{p.description}</Text>}
            <Textarea
                id={id}
                name={p.name}
                ref={textareaRef}
                data-cy="custom-textarea-input"
                placeholder={p.placeholder ?? "Type a message..."}
                value={p.currentValue ?? ""}
                onChange={(e) => p.onUpdate?.(e.target.value)}
                onKeyDown={p.onKeyDown}
                resize="none"
                borderRadius={p.borderRadius ?? "24px"}
                bg="white"
                px={4}
                py={3}
                fontSize="sm"
                minH={`${minH}px`}
                maxH={`${MAX_HEIGHT}px`}
                disabled={p.disabled}
                _focus={{
                    boxShadow: "none",
                    borderColor: "primary.500",
                    borderWidth: "1.5px",
                }}
                flex={p.flex}
                sx={{...scrollbar.styled}}
            />
            {p.error && <FormErrorMessage color="red" fontSize="14px" p={0} m={0} py="2px">
                {p.error}
            </FormErrorMessage>}
        </InputGroup>
    </FormControl>;
}

